import { useEffect, useMemo, useState } from 'react'
import {
  api, errorMessage,
  type MenuItem, type MenuScope, type MenuPatchInput,
} from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { useI18n, type TKey } from '@/lib/i18n'
import { cn } from '@/lib/cn'
import { PageError, PageLoading } from './Stats'

const TABS: { scope: MenuScope; labelKey: TKey }[] = [
  { scope: 'marketing', labelKey: 'menus.tab.marketing' },
  { scope: 'workspace', labelKey: 'menus.tab.workspace' },
  { scope: 'admin',     labelKey: 'menus.tab.admin' },
]

// Group rows by parent id and return them as a flat list ordered with each
// parent followed by its children — easier to render in the table than a
// real tree (we just indent children visually).
function flattenTree(items: MenuItem[]): Array<{ row: MenuItem; depth: number }> {
  const byParent = new Map<string | null, MenuItem[]>()
  for (const r of items) {
    const k = r.parentId ?? null
    const list = byParent.get(k) ?? []
    list.push(r)
    byParent.set(k, list)
  }
  const sortFn = (a: MenuItem, b: MenuItem) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code)
  const out: Array<{ row: MenuItem; depth: number }> = []
  const walk = (parentId: string | null, depth: number) => {
    const children = (byParent.get(parentId) ?? []).slice().sort(sortFn)
    for (const c of children) {
      out.push({ row: c, depth })
      walk(c.id, depth + 1)
    }
  }
  walk(null, 0)
  return out
}

export function MenusScreen() {
  const toast = useToast()
  const { t } = useI18n()
  const [scope, setScope] = useState<MenuScope>('marketing')
  const [rows, setRows] = useState<MenuItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Rows are read-only by default. The user clicks Edit on a row to enter edit
  // mode; one row at a time. drafts holds the in-flight edit; editingId marks
  // which row is open. Cancel clears the draft, Save commits then clears.
  const [drafts, setDrafts] = useState<Record<string, MenuPatchInput>>({})
  const [editingId, setEditingId] = useState<string | null>(null)

  function load() {
    setBusy(true); setError(null)
    setDrafts({}); setEditingId(null)
    api.listMenus(scope)
      .then((r) => setRows(r.items))
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }
  useEffect(() => { load() }, [scope])

  const ordered = useMemo(() => rows ? flattenTree(rows) : null, [rows])

  function setDraft(id: string, patch: MenuPatchInput) {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }))
  }

  async function commit(row: MenuItem) {
    const patch = drafts[row.id]
    if (!patch || Object.keys(patch).length === 0) {
      // Nothing changed — just leave edit mode quietly.
      setEditingId(null)
      return
    }
    try {
      await api.patchMenu(row.id, patch)
      setRows((prev) => prev?.map((r) => r.id === row.id ? { ...r, ...patch, labelEn: patch.labelEn === undefined ? r.labelEn : (patch.labelEn ?? null) } as MenuItem : r) ?? null)
      setDrafts((d) => { const { [row.id]: _, ...rest } = d; return rest })
      setEditingId(null)
      toast.success(row.code, t('menus.toast.saved'))
    } catch (e) {
      toast.error(t('menus.toast.saveFailed'), errorMessage(e))
    }
  }

  function startEdit(id: string) {
    // Switching to a new row discards any unsaved draft on the previous row,
    // mirroring how a single editing slot would work.
    setDrafts((d) => {
      const next = { ...d }
      if (editingId && editingId !== id) delete next[editingId]
      return next
    })
    setEditingId(id)
  }

  function cancelEdit(id: string) {
    setDrafts((d) => { const { [id]: _, ...rest } = d; return rest })
    setEditingId(null)
  }

  if (error) return <PageError msg={error}/>
  if (!rows && busy) return <PageLoading/>

  return (
    <div className="p-6 space-y-5">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">{t('menus.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('menus.subtitle', { n: rows?.length ?? 0 })}</p>
      </header>

      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map((tb) => (
          <button
            key={tb.scope}
            type="button"
            onClick={() => setScope(tb.scope)}
            className={cn(
              'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              scope === tb.scope
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      <Table>
        <THead>
          <Tr>
            <Th>{t('menus.col.code')}</Th>
            <Th>{t('menus.col.labelZh')}</Th>
            <Th>{t('menus.col.labelEn')}</Th>
            <Th className="pr-4" style={{ minWidth: '7rem' }}>{t('menus.col.sortOrder')}</Th>
            <Th className="w-20">{t('menus.col.visible')}</Th>
            <Th className="w-20">{t('menus.col.active')}</Th>
            <Th className="text-right pr-4">{t('menus.col.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {ordered?.map(({ row, depth }) => {
            const isEditing = editingId === row.id
            const draft = drafts[row.id] ?? {}
            // Effective values — either live draft (when editing) or stored row.
            const labelZh = isEditing ? (draft.labelZh ?? row.labelZh) : row.labelZh
            const labelEn = isEditing
              ? (draft.labelEn !== undefined ? (draft.labelEn ?? '') : (row.labelEn ?? ''))
              : (row.labelEn ?? '')
            const sortOrder = isEditing ? (draft.sortOrder ?? row.sortOrder) : row.sortOrder
            const visible = isEditing ? (draft.visible ?? (row.visible === 1 ? 1 : 0)) : (row.visible === 1 ? 1 : 0)
            const active = isEditing ? (draft.active ?? (row.active === 1 ? 1 : 0)) : (row.active === 1 ? 1 : 0)
            return (
              <Tr key={row.id}>
                <Td className="font-mono text-xs">
                  <span style={{ paddingLeft: depth * 16 }}>{depth > 0 && '└ '}{row.code}</span>
                </Td>
                <Td>
                  {isEditing ? (
                    <Input
                      value={labelZh}
                      onChange={(e) => setDraft(row.id, { labelZh: e.target.value })}
                      className="h-8"
                      autoFocus
                    />
                  ) : (
                    <span className="font-medium">{labelZh}</span>
                  )}
                </Td>
                <Td>
                  {isEditing ? (
                    <Input
                      value={labelEn}
                      onChange={(e) => setDraft(row.id, { labelEn: e.target.value || null })}
                      className="h-8"
                      placeholder="—"
                    />
                  ) : (
                    <span className="text-muted-foreground">{labelEn || '—'}</span>
                  )}
                </Td>
                <Td className="pr-4">
                  {isEditing ? (
                    <Input
                      type="number"
                      value={String(sortOrder)}
                      onChange={(e) => setDraft(row.id, { sortOrder: Number(e.target.value) || 0 })}
                      className="h-8 tabular-nums w-20"
                    />
                  ) : (
                    <span className="tabular-nums text-muted-foreground">{sortOrder}</span>
                  )}
                </Td>
                <Td>
                  <input
                    type="checkbox"
                    checked={visible === 1}
                    disabled={!isEditing}
                    onChange={(e) => setDraft(row.id, { visible: e.target.checked ? 1 : 0 })}
                  />
                </Td>
                <Td>
                  <input
                    type="checkbox"
                    checked={active === 1}
                    disabled={!isEditing}
                    onChange={(e) => setDraft(row.id, { active: e.target.checked ? 1 : 0 })}
                  />
                </Td>
                <Td className="text-right pr-4 space-x-1.5 whitespace-nowrap">
                  {isEditing ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => cancelEdit(row.id)}>
                        {t('action.cancel')}
                      </Button>
                      <Button size="sm" onClick={() => commit(row)}>
                        {t('menus.action.save')}
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => startEdit(row.id)}>
                      {t('action.edit')}
                    </Button>
                  )}
                </Td>
              </Tr>
            )
          })}
        </TBody>
      </Table>
    </div>
  )
}
